import React, { useState, useRef, useEffect } from 'react';
import { Send, Mic, MicOff, Volume2, VolumeX, Loader2, MessageSquare } from 'lucide-react';
import { AIOutput } from '@/lib/gemini';
import Latex from 'react-latex-next';
import { motion, AnimatePresence } from 'motion/react';

interface ChatSectionProps {
  aiData: AIOutput;
  language?: string;
}

const getLangCode = (lang: string) => {
  const map: Record<string, string> = {
    "English": "en-US",
    "Hindi": "hi-IN",
    "Bengali": "bn-IN",
    "Gujarati": "gu-IN",
    "Kannada": "kn-IN",
    "Malayalam": "ml-IN",
    "Marathi": "mr-IN",
    "Odia": "or-IN",
    "Punjabi": "pa-IN",
    "Tamil": "ta-IN",
    "Telugu": "te-IN",
    "Spanish": "es-ES",
    "French": "fr-FR",
    "German": "de-DE",
    "Mandarin": "zh-CN",
    "Japanese": "ja-JP",
    "Korean": "ko-KR",
    "Arabic": "ar-SA",
    "Portuguese": "pt-PT",
    "Russian": "ru-RU",
    "Italian": "it-IT"
  };
  return map[lang] || "en-US";
};

type Message = {
  id: string;
  role: 'user' | 'model';
  text: string;
};

export function ChatSection({ aiData, language = "English" }: ChatSectionProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'model',
      text: "Hello! I'm Professor X. Do you have any doubts about the steps we just covered? Feel free to ask or use your microphone!"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<any[]>([]);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    // Initialize Speech Recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        handleSend(transcript);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error("Speech recognition error", event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setMessages(prev => [...prev, {
            id: Date.now().toString(),
            role: 'model',
            text: "I couldn't access your microphone. Please make sure you've allowed microphone access in your browser settings and that no other app is using it."
          }]);
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [aiData]);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const speak = async (text: string) => {
    if (!voiceEnabled) return;
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    
    // Clean up markdown and latex for speech
    const cleanText = text
      .replace(/\\$\\$.*?\\$\\$/g, ' a math equation ')
      .replace(/\\$.*?\\$/g, ' a math expression ')
      .replace(/\\*\\*/g, '')
      .replace(/\\*/g, '');

    // 1. Primary route: Call server-side /api/tts (Sarvam AI for Indian languages, ElevenLabs & Google fallback)
    try {
      const res = await fetch("/api/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: cleanText,
          language: language,
          stepIndex: 0
        })
      });

      const contentType = res.headers.get("content-type") || "";
      if (res.ok && (contentType.includes("audio/") || contentType.includes("application/octet-stream"))) {
        const blob = await res.blob();
        if (blob && blob.size > 100) {
          const url = URL.createObjectURL(blob);
          if (!audioRef.current) audioRef.current = new Audio();
          audioRef.current.src = url;
          audioRef.current.play().catch(e => console.error("Error playing Chat audio:", e));
          return;
        }
      }
    } catch (apiErr) {
      console.warn("[ChatSection] /api/tts call failed, attempting client fallback:", apiErr);
    }
      
    try {
      let rawApiKey = (import.meta as any).env.VITE_ELEVENLABS_API_KEY || "sk_320b81a43e6c58d00c5ea968e7288194ce54b7877f6f952c";
      const fallbackKey = "sk_320b81a43e6c58d00c5ea968e7288194ce54b7877f6f952c";
      if (rawApiKey) {
        const trimmed = rawApiKey.trim();
        if (trimmed === "" || trimmed.includes("•") || trimmed.includes("*") || trimmed.includes("...") || [...trimmed].some(c => c.charCodeAt(0) > 127)) {
          rawApiKey = fallbackKey;
        } else {
          rawApiKey = trimmed;
        }
      } else {
        rawApiKey = fallbackKey;
      }
      const voiceCandidates = [
        "pNInz6obpgDQGcFmaJgB", // Adam (Bold, clear, calm, deep male narration - Priority 1)
        "JBFqnCBsd6RMkjVDRZzb", // George (Warm, calm, authoritative deep male teacher)
        "bIHbv24MWmeRgasZH58o", // Marcus (Deep, resonant, bold male)
        "onwK4e9ZLuTAKqWW03F9", // Daniel (Authoritative British deep male)
        "TX3LPaxmHKxFdv7VOQHJ", // Liam (Articulate, clear, calm male)
        "VR6AewLTigWG4xSOukaG"  // Arnold (Crisp, authoritative male)
      ];

      let response: Response | null = null;
      for (const vId of voiceCandidates) {
        const res = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${vId}`, {
          method: 'POST',
          headers: {
            'Accept': 'audio/mpeg',
            'Content-Type': 'application/json',
            'xi-api-key': rawApiKey
          },
          body: JSON.stringify({
            text: cleanText,
            model_id: "eleven_multilingual_v2",
            voice_settings: { stability: 0.75, similarity_boost: 0.85, style: 0.0, use_speaker_boost: true }
          })
        });

        if (res.ok) {
          response = res;
          break;
        }
      }

      if (!response || !response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      audioRef.current.src = url;
      audioRef.current.play().catch(e => console.error("Error playing audio:", e));
    } catch (error) {
      console.error("Error fetching ElevenLabs audio, falling back to browser TTS:", error);
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(cleanText);
        utterance.lang = getLangCode(language);
        const langCode = getLangCode(language);
        const isIndicCode = langCode.startsWith('hi') || langCode.startsWith('bn') || langCode.startsWith('mr') || langCode.startsWith('ta') || langCode.startsWith('te');
        utterance.rate = isIndicCode ? 0.92 : 0.95;
        utterance.pitch = 0.78; // Deep, calm, bold male pitch
        
        // Try to find a voice for the specific language
        const voices = window.speechSynthesis.getVoices();
        let langPrefix = langCode.split('-')[0];
        let searchPrefix = langPrefix;
        
        // Strict classification helpers: Never allow female voices
        const isFemaleVoiceName = (name: string) => {
          const n = name.toLowerCase();
          const femaleKeywords = [
            'female', 'woman', 'girl', 'child', 'zira', 'samantha', 'vicky', 'serena', 'karen', 
            'moira', 'tessa', 'victoria', 'hazel', 'susan', 'catherine', 'fiona', 'veena', 
            'leena', 'heera', 'swara', 'aditi', 'lekha', 'geeta', 'shruti', 'jenny', 'aria', 
            'sonia', 'natasha', 'libby', 'mia', 'ava', 'steffi', 'clara', 'charlotte', 'alice', 
            'helena', 'monica', 'laura', 'julie', 'eva', 'yuki', 'ting-ting', 'kyoko', 'sin-ji', 
            'mei-jia', 'amira', 'zoe', 'salli', 'joanna', 'kendra', 'ivy', 'kimberly', 'nicole',
            'linda', 'amy', 'emma', 'olivia', 'ruth', 'elizabeth', 'sarah', 'mary'
          ];
          return femaleKeywords.some(kw => n.includes(kw));
        };

        const isMaleVoiceName = (name: string) => {
          if (isFemaleVoiceName(name)) return false;
          const n = name.toLowerCase();
          const maleKeywords = [
            'male', 'man', 'david', 'george', 'daniel', 'alex', 'tom', 'mark', 'guy', 
            'arthur', 'aaron', 'richard', 'james', 'john', 'peter', 'paul', 'rishi', 
            'ravi', 'neel', 'deep', 'baritone', 'bold', 'calm', 'professor', 'brian', 
            'russell', 'oliver', 'fred', 'lee', 'natural (male)', 'standard-b',
            'standard-d', 'wavenet-b', 'wavenet-d', 'neural2-d', 'neural2-j', 'journey-d'
          ];
          return maleKeywords.some(kw => n.includes(kw));
        };

        const langVoices = voices.filter(v => v.lang.startsWith(langPrefix));
        const hasMaleSpecificVoice = langVoices.some(v => isMaleVoiceName(v.name));

        // Language fallback: If no specific MALE voice, try matching script/region fallbacks
        if (!hasMaleSpecificVoice && langPrefix === 'mr') {
           searchPrefix = 'hi'; // Marathi fallback to Hindi
        }

        // STRICT FILTERING: Only male/man voices
        const filteredVoices = voices.filter(v => v.lang.startsWith(searchPrefix) && !isFemaleVoiceName(v.name));

        const voice = filteredVoices.find(v => isMaleVoiceName(v.name))
                   || filteredVoices.find(v => v.name.toLowerCase().includes('google') && !isFemaleVoiceName(v.name))
                   || filteredVoices.find(v => v.name.toLowerCase().includes('deep') || v.name.toLowerCase().includes('bold'))
                   || filteredVoices[0]
                   || voices.find(v => v.lang.startsWith('en') && isMaleVoiceName(v.name))
                   || voices.find(v => v.lang.startsWith('en') && !isFemaleVoiceName(v.name));
                   
        if (voice) {
          utterance.voice = voice;
        } else {
          const fallbackEnglish = voices.find(v => isMaleVoiceName(v.name)) || voices.find(v => !isFemaleVoiceName(v.name));
          if (fallbackEnglish) utterance.voice = fallbackEnglish;
        }

        utterance.onerror = (err: any) => {
          console.error("Chat SpeechSynthesis error:", err.error || err);
        };
        
        // Ensure clean state
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }

        window.speechSynthesis.speak(utterance);
      }
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const handleSend = async (textToSend: string = input) => {
    if (!textToSend.trim() || isLoading) return;

    const userMsg: Message = { id: Date.now().toString(), role: 'user', text: textToSend };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const stepsContext = aiData.steps.map(s => `Step ${s.step}: ${s.explanation} (Math: ${s.board_content})`).join('\n');
      
      let oneshotContextStr = '';
      if ((aiData as any).is_oneshot_mode) {
        const ch = (aiData as any).chapter_analysis || {};
        const qList = (aiData as any).important_questions || [];
        oneshotContextStr = `
ONESHOT MASTERCLASS SESSION CONTEXT:
- Mode: OneShot Learning
- Subject: ${aiData.subject}
- Chapter / Topic: ${aiData.topic}
- Class Level: ${(aiData as any).subject_info?.class_level || 'N/A'}
- Board: ${(aiData as any).subject_info?.board || 'N/A'}
- Overview: ${ch.overview || ''}
- Major Concepts Taught: ${Array.isArray(ch.major_concepts) ? ch.major_concepts.join(', ') : ''}
- Formulas Taught: ${Array.isArray(aiData.formulas) ? aiData.formulas.join('; ') : ''}
- Common Mistakes Discussed: ${Array.isArray(ch.common_mistakes) ? ch.common_mistakes.join('; ') : ''}
- Exam Questions Covered: ${qList.map((q: any) => `[Q${q.id || ''}: ${q.question}]`).join('; ')}
        `;
      }

      const systemInstruction = `You are Professor X, a professional, calm, and encouraging teacher.
The student just completed a video masterclass on: "${aiData.topic || aiData.question}"
${oneshotContextStr}
Here are the steps that were explained in the video:
${stepsContext}

Your goal is to answer any follow-up doubts the student has about this topic, the steps, formulas, or questions covered.
If the student says e.g. "I didn't understand concept 2" or "Explain question 1 again", refer directly to the concepts/questions from this session without restarting from zero.
All your responses MUST be strictly in ${language} for both the display text and the spoken narration.
Keep your answers concise, conversational, and easy to understand.
Use LaTeX for math expressions, wrapping inline math in $...$ and block math in $$...$$.
Do not repeat the whole solution, just address their specific confusion.

CRITICAL INSTRUCTION:
You MUST return your response in JSON format with exactly two fields:
{
  "text": "Your response in ${language} (this will be displayed as text)",
  "audio_text": "Your response in ${language} (this will be spoken aloud)"
}
If the requested language is English, both fields will be in English.`;

      const googleToken = typeof window !== 'undefined' ? localStorage.getItem('google_access_token') : null;
      const userApiKey = typeof window !== 'undefined' ? localStorage.getItem('gemini_api_key') : null;
      const sessionId = typeof window !== 'undefined' ? localStorage.getItem('google_session_id') : null;

      const executeChatFetch = async () => {
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (userApiKey && userApiKey.trim()) {
          headers["x-gemini-api-key"] = userApiKey.trim();
        }
        if (googleToken && googleToken.trim()) {
          headers["Authorization"] = `Bearer ${googleToken.trim()}`;
          headers["x-google-access-token"] = googleToken.trim();
          headers["x-goog-user-project"] = "professor-x-visuals";
        }
        if (sessionId && sessionId.trim()) {
          headers["x-session-id"] = sessionId.trim();
        }

        return await fetch("/api/gemini/chat", {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({
            message: textToSend,
            history: historyRef.current,
            systemInstruction,
            responseMimeType: "application/json"
          }),
        });
      };

      let response = await executeChatFetch();

      if (!response.ok) {
        let errorMessage = "Failed to get response from server";
        try {
          const errorData = await response.json();
          const rawErrStr = errorData.error ? (typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error)) : "";
          if (
            rawErrStr.includes("currently experiencing high demand") || 
            rawErrStr.includes("UNAVAILABLE") || 
            rawErrStr.includes("503") ||
            rawErrStr.includes("currently busy")
          ) {
            errorMessage = "our server is currently busy try soon";
          } else {
            errorMessage = errorData.error || errorMessage;
          }
        } catch (e) {
          // Ignore
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const responseText = data.text || "";
      
      let modelText = "I'm sorry, I couldn't process that.";
      let audioText = "";
      
      try {
        const parsed = JSON.parse(responseText || "{}");
        modelText = parsed.text || parsed.english_text || responseText;
        audioText = parsed.audio_text || modelText;
      } catch (e) {
        modelText = responseText || "I'm sorry, I couldn't process that.";
        audioText = modelText;
      }
      
      // Update history
      historyRef.current = [
        ...historyRef.current,
        { role: 'user', parts: [{ text: textToSend }] },
        { role: 'model', parts: [{ text: responseText }] }
      ];

      const modelMsg: Message = { id: (Date.now() + 1).toString(), role: 'model', text: modelText };
      setMessages(prev => [...prev, modelMsg]);
      
      if (voiceEnabled) {
        speak(audioText);
      }
    } catch (error: any) {
      console.error("Chat error:", error);
      const errLower = (error.message || "").toLowerCase();
      const isAuthError = 
        errLower.includes("invalid authentication credentials") || 
        errLower.includes("expected oauth 2 access token") || 
        errLower.includes("401");
      const isHighDemand = error.message?.includes("currently experiencing high demand") || 
                           error.message?.includes("UNAVAILABLE") || 
                           error.message?.includes("503") || 
                           error.message?.includes("currently busy");
      setMessages(prev => [...prev, { 
        id: Date.now().toString(), 
        role: 'model', 
        text: isAuthError
          ? "Session expired or authentication failed. Please log out and sign in again, or switch to a different Google account."
          : isHighDemand 
            ? "our server is currently busy try soon"
            : `Sorry, I encountered an error: ${error.message || "Unknown error"}. Please make sure your API key is set correctly in the server environment.` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatLatex = (content: string) => {
    return content;
  };

  return (
    <div className="w-full bg-slate-900 rounded-2xl shadow-sm border border-slate-800 overflow-hidden mt-8 flex flex-col max-h-[960px] h-fit sticky top-24">
      <div className="bg-indigo-600 px-5 py-3 flex items-center justify-between text-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          <h3 className="font-bold text-base">Doubt Resolution Chat</h3>
        </div>
        <button 
          onClick={() => {
            setVoiceEnabled(!voiceEnabled);
            if (voiceEnabled) {
              if (audioRef.current) audioRef.current.pause();
              if ('speechSynthesis' in window) window.speechSynthesis.cancel();
            }
          }}
          className="p-2 hover:bg-white/20 rounded-full transition-colors"
          title={voiceEnabled ? "Mute Professor X" : "Unmute Professor X"}
        >
          {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950">
        <AnimatePresence>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[80%] rounded-2xl p-4 ${
                  msg.role === 'user' 
                    ? 'bg-indigo-600 text-white rounded-tr-sm' 
                    : 'bg-slate-900 border border-slate-800 text-slate-200 rounded-tl-sm shadow-sm'
                }`}
              >
                {msg.role === 'model' && <div className="text-xs font-bold text-indigo-400 mb-1">Professor X</div>}
                <div className="prose prose-sm max-w-none prose-invert">
                  <Latex strict={false}>{formatLatex(msg.text)}</Latex>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {isLoading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl rounded-tl-sm p-4 shadow-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
              <span className="text-sm text-slate-400">Professor X is thinking...</span>
            </div>
          </motion.div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 bg-slate-900 border-t border-slate-800 flex-shrink-0">
        <form 
          onSubmit={(e) => { e.preventDefault(); handleSend(); }}
          className="flex items-center gap-2"
        >
          <button
            type="button"
            onClick={toggleListening}
            className={`p-2.5 rounded-full flex-shrink-0 transition-colors ${
              isListening ? 'bg-red-900/50 text-red-400 animate-pulse' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
            title="Hold to speak"
          >
            {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question..."
            className="flex-1 bg-slate-800 border-transparent focus:bg-slate-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 rounded-xl px-4 py-2.5 outline-none transition-all text-slate-100 placeholder:text-slate-500 text-sm"
            disabled={isLoading || isListening}
          />
          
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="p-2.5 bg-indigo-600 text-white rounded-xl flex-shrink-0 hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
